import { useCallback, useEffect, useRef, useState } from "react";
import { searchCrateV2 } from "@services/api";
import { SEARCH_DEBOUNCE_MS } from "../constants";

// Debounced wrapper around GET /search-crate-v2.
// Mirrors crate-cx-items.vue's getCrate(debounce 500ms) — we use 400ms which
// feels snappier on phone keyboards. Auto-select-on-single-result is left to
// the caller so it can also drive the input field.

const useCrateSearch = ({ tab = "today", onAutoSelect } = {}) => {
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const isMounted = useRef(true);
  // Stale-fetch guard. Only the latest fire() may write state.
  const reqId = useRef(0);

  useEffect(
    () => () => {
      isMounted.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const fire = useCallback(
    async (term) => {
      const my = ++reqId.current;
      setSearching(true);
      try {
        const res = await searchCrateV2({
          searchTerm: String(term || "").trim(),
          tab,
        });
        const list = res?.data?.crate_ids || [];
        if (!isMounted.current || my !== reqId.current) return;
        setResults(list);
        if (
          typeof onAutoSelect === "function" &&
          Array.isArray(list) &&
          list.length === 1
        ) {
          onAutoSelect(list[0]);
        }
      } catch {
        if (isMounted.current && my === reqId.current) setResults([]);
      } finally {
        if (isMounted.current && my === reqId.current) setSearching(false);
      }
    },
    [tab, onAutoSelect],
  );

  const search = useCallback(
    (term) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => fire(term), SEARCH_DEBOUNCE_MS);
    },
    [fire],
  );

  // Fire one immediately on mount (web does this in mounted()).
  useEffect(() => {
    fire("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return { results, searching, search };
};

export default useCrateSearch;
